import { ArrowDown, ArrowLeftRight, Sun, Shield, Cpu, Battery, Plug, Zap, Download } from "lucide-react";

export function StatCard({ label, value, hint, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5">
      <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <p className="mt-2 font-display text-2xl font-extrabold text-slate-900" data-testid={testid}>{value}</p>
      {hint && <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{hint}</p>}
    </div>
  );
}

export function SystemFlow({ architecture }) {
  const steps = [
    { icon: Sun, label: "Solar Panels" },
    { icon: Shield, label: "Protection" },
    { icon: Cpu, label: "Hybrid Inverter" },
    { icon: Battery, label: "Battery Bank" },
    { icon: Plug, label: "Distribution Board" },
    { icon: Zap, label: "Your Appliances" },
  ];
  return (
    <div data-testid="system-flow" className="rounded-lg border border-slate-200 bg-white p-6">
      <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">System Architecture</p>
      <p className="mt-2 font-display text-lg font-bold text-slate-900">{architecture}</p>
      <div className="mt-5 flex flex-col items-center gap-1.5">
        {steps.map((s, i) => (
          <div key={s.label} className="flex flex-col items-center gap-1.5 w-full max-w-xs">
            <div className="flex w-full items-center gap-3 rounded-md border border-slate-200 bg-mist px-4 py-3">
              <s.icon className="h-4 w-4 text-emerald-600 shrink-0" aria-hidden="true" />
              <span className="text-sm font-medium text-slate-800">{s.label}</span>
            </div>
            {i === 2 && <ArrowLeftRight className="h-4 w-4 text-emerald-600" aria-hidden="true" />}
            {i !== 2 && i < steps.length - 1 && <ArrowDown className="h-4 w-4 text-emerald-600" aria-hidden="true" />}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ReportView({ calculation, order, onDownload, downloading }) {
  const f = calculation.results.full;
  const p = calculation.profile;

  return (
    <div data-testid="full-report" className="space-y-10">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-emerald-300 bg-emerald-50 p-5">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-700">Order {order.reference} · Paid</p>
          <h3 className="mt-1 font-display text-xl font-bold text-slate-900">Your BNM Solar System Recommendation</h3>
          <p className="mt-1 text-sm text-slate-600">Preliminary system recommendation — final sizing is verified through a BNM site assessment.</p>
        </div>
        <button
          onClick={onDownload}
          disabled={downloading}
          data-testid="report-download-pdf"
          className="inline-flex items-center gap-2 rounded-md bg-obsidian px-6 py-3 font-semibold text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-pine hover:text-volt disabled:opacity-60"
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          {downloading ? "Preparing…" : "Download PDF Report"}
        </button>
      </div>

      <div>
        <h4 className="font-display text-lg font-bold text-slate-900">Energy Profile</h4>
        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Daily consumption" value={`${f.energy.daily_kwh} kWh`} hint="Total energy your appliances use per day" testid="report-daily-kwh" />
          <StatCard label="Monthly consumption" value={`${f.energy.monthly_kwh} kWh`} hint="Estimated 30-day usage" testid="report-monthly-kwh" />
          <StatCard label="Peak load" value={`${f.energy.peak_w} W`} hint="Maximum power your appliances may require at the same time" testid="report-peak" />
          <StatCard label="Surge load" value={`${f.energy.surge_w} W`} hint="Start-up power for motors and compressors" testid="report-surge" />
        </div>
      </div>

      <div>
        <h4 className="font-display text-lg font-bold text-slate-900">Recommended System</h4>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-lg border border-slate-200 bg-white p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">Solar Array</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-slate-900" data-testid="report-solar-kw">{f.solar.installed_kw} kW</p>
            <p className="mt-2 text-sm text-slate-600">{f.solar.panels} × {f.solar.panel_w} W panels</p>
            <p className="mt-1 text-xs text-slate-500">Required generation: {f.solar.array_kw} kW</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">Battery Storage</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-slate-900" data-testid="report-battery-kwh">{f.battery.installed_kwh} kWh</p>
            <p className="mt-2 text-sm text-slate-600">{f.battery.units} × {f.battery.unit_name}</p>
            <p className="mt-1 text-xs text-slate-500">Required: {f.battery.needed_kwh} kWh ({f.battery.chemistry}) · {f.energy.autonomy_days} day(s) autonomy — how long batteries power your essentials without solar input</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">Inverter</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-slate-900" data-testid="report-inverter-kw">{f.inverter.recommended_kw} kW</p>
            <p className="mt-2 text-sm text-slate-600">Pure sine wave hybrid · {f.inverter.system_voltage} system</p>
            <p className="mt-1 text-xs text-slate-500">Surge capacity ≈ {f.inverter.surge_capacity_w} W</p>
          </div>
        </div>
        <div className="mt-3">
          <SystemFlow architecture={f.architecture} />
        </div>
      </div>

      {f.warnings.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-5 space-y-2" data-testid="report-warnings">
          {f.warnings.map((w, i) => (
            <p key={i} className="text-sm text-amber-800 leading-relaxed">{w}</p>
          ))}
        </div>
      )}

      <div>
        <h4 className="font-display text-lg font-bold text-slate-900">Preliminary Cable & Protection</h4>
        <p className="mt-1 text-xs text-slate-500">Preliminary engineering estimate — final cable sizing must be verified during site assessment.</p>
        <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead className="bg-obsidian text-white">
              <tr>{["Run", "Current", "Distance", "Cable size"].map((h) => <th key={h} className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.14em] font-medium">{h}</th>)}</tr>
            </thead>
            <tbody>
              {[["Solar array → inverter (DC)", f.cables.solar], ["Battery → inverter (DC)", f.cables.battery], ["Inverter → distribution board (AC)", f.cables.ac]].map(([label, c], i) => (
                <tr key={label} className={i % 2 ? "bg-white" : "bg-mist"}>
                  <td className="px-5 py-3.5 font-medium text-slate-900">{label}</td>
                  <td className="px-5 py-3.5 text-slate-600">{c.current_a} A</td>
                  <td className="px-5 py-3.5 text-slate-600">{c.distance_m} m</td>
                  <td className="px-5 py-3.5 font-semibold text-emerald-700">{c.mm2} mm² copper</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="DC isolator" value={`${f.protection.dc_isolator_a} A`} />
          <StatCard label="Battery breaker" value={`${f.protection.battery_breaker_a} A`} />
          <StatCard label="AC breaker" value={`${f.protection.ac_breaker_a} A`} />
          <StatCard label="Surge protection" value="Type II" />
        </div>
      </div>

      <div>
        <h4 className="font-display text-lg font-bold text-slate-900">Estimated Bill of Materials</h4>
        <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200" data-testid="report-bom">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="bg-obsidian text-white">
              <tr>{["Component", "Specification", "Qty", "Notes"].map((h) => <th key={h} className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.14em] font-medium">{h}</th>)}</tr>
            </thead>
            <tbody>
              {f.bom.map((r, i) => (
                <tr key={r.component} className={i % 2 ? "bg-white" : "bg-mist"}>
                  <td className="px-5 py-3.5 font-medium text-slate-900">{r.component}</td>
                  <td className="px-5 py-3.5 text-slate-600">{r.specification}</td>
                  <td className="px-5 py-3.5 text-slate-900 font-semibold">{r.quantity}</td>
                  <td className="px-5 py-3.5 text-slate-500 text-xs">{r.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <details className="rounded-lg border border-slate-200 bg-white p-5 group">
        <summary className="cursor-pointer font-display font-bold text-slate-900" data-testid="report-how-calculated">
          How we calculated this
        </summary>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(f.assumptions).map(([k, v]) => (
            <div key={k} className="rounded-md bg-mist p-3.5">
              <p className="text-[11px] text-slate-500">{k.replace(/_/g, " ")}</p>
              <p className="mt-0.5 text-sm font-semibold text-slate-900">{typeof v === "number" && v < 1 ? `${Math.round(v * 100)}%` : v}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs leading-relaxed text-slate-500">
          Daily energy = Σ (quantity × watts × hours × days/7). Solar array = daily energy ÷ system efficiency ÷ peak sun hours.
          Battery = backup energy × autonomy ÷ depth of discharge ÷ battery efficiency × design margin. Inverter = peak load × design margin,
          checked against surge. Cable size from current, distance and {f.assumptions.voltage_drop_pct}% allowed voltage drop.
          Depth of discharge is the usable portion of battery capacity.
        </p>
      </details>

      <p className="rounded-lg border border-slate-200 bg-mist p-5 text-xs leading-relaxed text-slate-500">
        This is a preliminary system recommendation from the BNM Solar Energy Calculator. Final system sizing depends on actual site
        conditions, appliance specifications, solar irradiation, cable distances, installation conditions and battery technology, and must be
        verified through a BNM site assessment and qualified technical review before installation. Project type: {p.customer_type || "—"}.
      </p>
    </div>
  );
}
